import { ReactElement, useEffect } from 'react';
import { useBreadcrumbs } from '@client/breadcrumbs/hooks';
import { useOnInitialRender, usePrevious } from '@client/common/hooks';
import { parse } from '@client/common/utils/queryParams';
import { useTranslate } from '@client/i18n/hooks';
import { BoxError } from '@client/myAccount/common/components/BoxError';
import { MyAccount } from '@client/myAccount/common/components/MyAccount';
import { MY_ACCOUNT_BACK_PAGE, MY_ACCOUNT_MENU_ITEM } from '@client/myAccount/common/utils/myAccountMenu';
import { useClearRmasAction, useSearchRmasAction, useSearchRmasData } from '@client/myAccount/rma/hooks';
import { ROUTES } from '@client/routes/components/Router/routes';
import { useUrlCreator } from '@client/routes/hooks';
import { RemoteRecordError } from 'b2b-apps/core/common/components/RemoteRecordHandler';
import { SearchPage } from '../SearchPage/SearchPage';

export const SearchPageContainer = (): ReactElement => {
  const searchParams = parse();
  const t = useTranslate();
  const { [MY_ACCOUNT_BACK_PAGE]: backPage, invoiceId } = searchParams;
  const searchRmasAction = useSearchRmasAction();
  const clearRmasAction = useClearRmasAction();
  const searchRmasData = useSearchRmasData();
  const urlCreator = useUrlCreator();

  const backButtonUrl = urlCreator(
    ROUTES.myAccountRmas,
    { page: backPage },
  );

  const previousStatusSearchRmasData = usePrevious(searchRmasData.status);

  useOnInitialRender(
    () => {
      if (!invoiceId) {
        clearRmasAction();
      }
    },
  );

  if (!invoiceId && previousStatusSearchRmasData === null && searchRmasData.isLoaded) {
    clearRmasAction();
  }

  useEffect(() => {
    if (invoiceId) {
      searchRmasAction(invoiceId);
    }
  },
  [invoiceId]);

  useBreadcrumbs([
    {
      title: t('My account'),
      url: urlCreator(ROUTES.myAccount),
    },
    {
      title: t('RMA list'),
      url: urlCreator(ROUTES.myAccountRmas),
    },
    {
      title: t('Create new RMA'),
    },
  ]);

  return (
    <MyAccount active={MY_ACCOUNT_MENU_ITEM.rma}>
      <RemoteRecordError remoteRecords={[searchRmasData]} errorComponent={(
        <BoxError title={t('Create new RMA')} remoteRecord={[searchRmasData]} />
      )}>
        <SearchPage
          backButtonUrl={backButtonUrl}
          searchRmasRecord={searchRmasData}
        />
      </RemoteRecordError>
    </MyAccount>
  );
};

